"use client";

import { useEffect, useRef } from "react";

interface Props {
  baseId: string;
}

const MIN_TIME_MS = 10000;
const VIEW_TTL_MS = 1000 * 60 * 60 * 6;

function getStorageKey(baseId: string) {
  return `viewed-base:${baseId}`;
}

function markViewed(baseId: string) {
  try {
    sessionStorage.setItem(
      getStorageKey(baseId),
      JSON.stringify({ sentAt: Date.now() })
    );
  } catch {}
}

function hasViewed(baseId: string) {
  try {
    const raw = sessionStorage.getItem(getStorageKey(baseId));
    if (!raw) return false;

    const parsed = JSON.parse(raw) as { sentAt?: number } | null;
    return (
      typeof parsed?.sentAt === "number" &&
      Date.now() - parsed.sentAt < VIEW_TTL_MS
    );
  } catch {
    return sessionStorage.getItem(getStorageKey(baseId)) === "1";
  }
}

export default function BaseViewTrackerClient({ baseId }: Props) {
  const hasSentRef = useRef(false);
  const hasScrolledRef = useRef(false);
  const hasBeenVisibleRef = useRef(false);
  const timerDoneRef = useRef(false);

  useEffect(() => {
    hasSentRef.current = false;
    hasScrolledRef.current = false;
    hasBeenVisibleRef.current = false;
    timerDoneRef.current = false;

    if (!baseId) return;
    if (hasViewed(baseId)) return;

    let timer: number | undefined;
    let observer: IntersectionObserver | null = null;
    let cancelled = false;

    const canSendNow = () =>
      timerDoneRef.current &&
      (hasScrolledRef.current || hasBeenVisibleRef.current);

    const send = async () => {
      if (cancelled || hasSentRef.current) return;

      hasSentRef.current = true;

      try {
        const res = await fetch("/api/views/increment", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ baseId }),
          keepalive: true,
          cache: "no-store",
        });

        if (res.ok) {
          markViewed(baseId);
          return;
        }

        hasSentRef.current = false;
      } catch {
        hasSentRef.current = false;
      }
    };

    const trySend = () => {
      if (!canSendNow()) return;
      void send();
    };

    const onScroll = () => {
      hasScrolledRef.current = true;
      trySend();
    };

    const onPageHide = () => {
      if (!canSendNow() || hasSentRef.current || hasViewed(baseId)) return;

      const payload = JSON.stringify({ baseId });
      const blob = new Blob([payload], { type: "application/json" });

      if (navigator.sendBeacon?.("/api/views/increment", blob)) {
        hasSentRef.current = true;
        markViewed(baseId);
      }
    };

    const start = () => {
      timer = window.setTimeout(() => {
        timerDoneRef.current = true;
        trySend();
      }, MIN_TIME_MS);

      window.addEventListener("scroll", onScroll, { passive: true, once: true });
      window.addEventListener("pagehide", onPageHide, { passive: true });

      const target = document.querySelector("[data-base-view-target]");
      if (!target) return;

      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry?.isIntersecting && entry.intersectionRatio >= 0.5) {
            hasBeenVisibleRef.current = true;
            trySend();
          }
        },
        { threshold: [0.5] }
      );

      observer.observe(target);
    };

    const ric =
      window.requestIdleCallback ??
      ((cb: IdleRequestCallback) => window.setTimeout(cb, 1));
    const cancelRic = window.cancelIdleCallback ?? window.clearTimeout;

    const idleId = ric(() => start(), { timeout: 2000 } as IdleRequestOptions);

    return () => {
      cancelled = true;
      cancelRic(idleId as number);
      if (timer) window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", onPageHide);
      observer?.disconnect();
    };
  }, [baseId]);

  return null;
}